/**
 * EngagementIndicator Component
 *
 * Traffic light indicator showing engagement health.
 * Uses green/yellow/red signals with optional labels and tooltips.
 */
import React from 'react'
import { cn } from '@/lib/utils'
import type { EngagementSignal } from '@/types/radar'

interface EngagementIndicatorProps {
  signal: EngagementSignal
  showLabel?: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const signalConfig: Record<EngagementSignal, { icon: string; label: string; bgClass: string; textClass: string }> = {
  green: {
    icon: '',
    label: 'High',
    bgClass: 'bg-green-500/10',
    textClass: 'text-green-500',
  },
  yellow: {
    icon: '',
    label: 'Medium',
    bgClass: 'bg-yellow-500/10',
    textClass: 'text-yellow-500',
  },
  red: {
    icon: '',
    label: 'Low',
    bgClass: 'bg-red-500/10',
    textClass: 'text-red-500',
  },
}

const sizeClasses = {
  sm: 'text-xs px-1.5 py-0.5',
  md: 'text-sm px-2 py-1',
  lg: 'text-base px-3 py-1.5',
}

export function EngagementIndicator({
  signal,
  showLabel = false,
  size = 'md',
  className,
}: EngagementIndicatorProps) {
  const config = signalConfig[signal]

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium',
        config.bgClass,
        config.textClass,
        sizeClasses[size],
        className
      )}
      title={`Engagement: ${config.label}`}
    >
      <span>{config.icon}</span>
      {showLabel && <span>{config.label}</span>}
    </span>
  )
}
